import React, { useContext } from 'react'
import moment, { Moment } from 'moment-timezone'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DesktopDateTimePicker, DesktopDateTimePickerProps } from '@mui/x-date-pickers/DesktopDateTimePicker'
import { LocalizationContext, useTenantTimezone } from './contexts'
import { normalizeToTenantTZ, uses12HourClock, withoutSeconds } from '../../utils/tenantDateTime'

interface TenantDateTimePickerProps extends Omit<DesktopDateTimePickerProps<Moment>, 'onChange' | 'value'> {
  value: Moment | null
  onChange: (value: Moment | null) => void
}

const TenantDateTimePicker: React.FC<TenantDateTimePickerProps> = ({ value, onChange, ...rest }) => {
  const tz = useTenantTimezone()
  const { date_format, time_format } = useContext(LocalizationContext)
  const defaultFormat = `${date_format} ${withoutSeconds(time_format || 'HH:mm')}`
  const ampm = uses12HourClock(time_format)

  const { disablePast, disableFuture, minDateTime, maxDateTime, ...other } = rest
  const tenantNow = moment.tz(tz)
  const effectiveMin = disablePast
    ? (minDateTime && minDateTime.isAfter(tenantNow) ? minDateTime : tenantNow)
    : minDateTime
  const effectiveMax = disableFuture
    ? (maxDateTime && maxDateTime.isBefore(tenantNow) ? maxDateTime : tenantNow)
    : maxDateTime

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
      <DesktopDateTimePicker
        format={defaultFormat}
        ampm={ampm}
        {...other}
        minDateTime={effectiveMin}
        maxDateTime={effectiveMax}
        value={value}
        onChange={picked => onChange(normalizeToTenantTZ(picked, tz))}
      />
    </LocalizationProvider>
  )
}

export default TenantDateTimePicker
